import React from 'react';
import { List, AutoSizer } from 'react-virtualized';
import { Tooltip } from 'antd';
import { ellipseMiddleString } from 'utils/helperMethods';
import Barchart from 'components/molecules/Barchart';
import { XAxisLabel, commonYAxisStyle } from 'components/organism/GrowthAnalysis/SalesAttributionDrilldownChart';
import { colorConstants } from 'config/colorConstants';
import GroupedBarChart from 'components/molecules/GroupedBarChart';

interface InfiniteChartComponentProps {
    height: number;
    chartItemOnClick: Function;
    barHeight: number;
    chartWidth: number;
    yAxisGraphWidth: number;
    selectedChartItem: any;
    showFullLabel: boolean;
    debouncedInfiniteScroller: _.DebouncedFunc<(e: any) => void>;
    chartOffsetRef: any;
    appConfig: any;
    fields?: string[];
    minBound: number;
    maxBound: number;
    rowCount: number;
    intervalFormatter: Function;
    tooltipFormatter: Function;
    intervalStyle: Function;
    xAxisLabelFormatter: Function;
    rowRenderer: (key: string, index: number, style: any) => any;
    chartContainer: string;
    isGroupedChart?: boolean;
}
const InfiniteChartComponent: React.FC<InfiniteChartComponentProps> = ({
    height,
    chartItemOnClick,
    barHeight,
    chartWidth,
    yAxisGraphWidth,
    selectedChartItem,
    showFullLabel,
    debouncedInfiniteScroller,
    chartOffsetRef,
    appConfig,
    fields,
    rowRenderer,
    intervalFormatter,
    intervalStyle,
    maxBound,
    minBound,
    tooltipFormatter,
    rowCount,
    xAxisLabelFormatter,
    chartContainer,
    isGroupedChart = false,
}) => {
    return (
        <>
            <div style={{ height }}>
                <div style={{ width: '100%', height }}>
                    <AutoSizer>
                        {({ width, height }) => (
                            <div className="App">
                                <List
                                    scrollToIndex={Math.max(0, chartOffsetRef?.current - 1)}
                                    onScroll={debouncedInfiniteScroller}
                                    width={width}
                                    height={height}
                                    rowHeight={barHeight}
                                    rowCount={rowCount}
                                    rowRenderer={({ key, index, style }) => {
                                        const item = rowRenderer(key, index, style);

                                        return (
                                            <div key={key} style={style}>
                                                <div
                                                    onClick={() => {
                                                        chartItemOnClick(item);
                                                    }}
                                                    className={`relative flex items-center cursor-pointer  `}
                                                    style={{
                                                        background:
                                                            selectedChartItem?.category === item?.category
                                                                ? colorConstants.grey
                                                                : 'none',
                                                    }}
                                                >
                                                    <div
                                                        title={item?.category}
                                                        style={{ ...commonYAxisStyle, wordBreak: 'break-all' }}
                                                        className="text-center p-[2px]"
                                                    >
                                                        {showFullLabel ? (
                                                            item?.category
                                                        ) : (
                                                            <Tooltip
                                                                title={item?.category}
                                                                placement="top"
                                                                color="white"
                                                            >
                                                                <div>{ellipseMiddleString(item?.category, 8, 4)}</div>
                                                            </Tooltip>
                                                        )}
                                                    </div>

                                                    {isGroupedChart ? (
                                                        <GroupedBarChart
                                                            padding={[0, 150, 0, 10]}
                                                            loading={false}
                                                            dataset={item?.attributes}
                                                            height={barHeight}
                                                            chartContainer={`${chartContainer}-group-Y-2-${index}`}
                                                            additionalStyle={{
                                                                width: chartWidth,
                                                            }}
                                                            hideLegend
                                                            minBound={minBound}
                                                            maxBound={maxBound}
                                                        />
                                                    ) : (
                                                        <Barchart
                                                            offsetXDistance={-6}
                                                            padding={[0, 10, 0, 20]}
                                                            barWidth={30}
                                                            dataset={item.attributes ? item.attributes : [item]}
                                                            height={barHeight}
                                                            width={chartWidth - yAxisGraphWidth}
                                                            minBound={minBound}
                                                            maxBound={maxBound}
                                                            intervalFormatter={(val: any) => intervalFormatter(val)}
                                                            tooltipFormatter={(xAxis, val) =>
                                                                tooltipFormatter(xAxis, val)
                                                            }
                                                            Xline={false}
                                                            colorKey="color"
                                                            transpose
                                                            intervalStyle={() => intervalStyle()}
                                                            fields={fields || []}
                                                            chartContainer={`${chartContainer}-Y-${index}`}
                                                            loading={false}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    }}
                                />
                            </div>
                        )}
                    </AutoSizer>
                </div>
            </div>

            <Barchart
                additionalStyle={{ marginLeft: yAxisGraphWidth - 10 }}
                padding={[0, 30, 20, 20]}
                dataset={[]}
                height={50}
                loading={false}
                chartContainer={`${chartContainer}-X-2-${1}`}
                fields={['category', 'sales']}
                minBound={minBound}
                maxBound={maxBound}
                width={chartWidth - yAxisGraphWidth + 20}
                transpose
                yAxisLabelFormatter={(val: any) => {
                    return xAxisLabelFormatter(val);
                }}
                YGrid={false}
                xAxisConfig={false}
                range={[1, 0]} // this will ensure the scale in revers order
                colorKey={'color'}
            />

            <XAxisLabel absTile={{ label: 'Elasticity' }} appConfig={appConfig} />
        </>
    );
};

export default InfiniteChartComponent;
